import * as React from 'react';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { getCatalogSellerId, getCatalogStartTime } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLiveCatalogStatus } from '@/redux/modules/console';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import useInterval from '@/hooks/useInterval';
import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';

/**
 * If a live auction's status changes to "notLoaded" or saleStartTs changes, refetch the live catalog status every 10 seconds
 *
 * This hook refreshes the console when an auction is reloaded or it's start time is moved
 */
const useAuctionStatusChecker = (catalogId: number) => {
    const dispatch = useAppDispatch();

    // track prev liveCatalogStatus
    const liveCatalogStatus = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const prevLiveCatalogStatus = usePrevious(liveCatalogStatus);
    const auctionHasUnLoaded =
        prevLiveCatalogStatus && prevLiveCatalogStatus !== 'notLoaded' && liveCatalogStatus === 'notLoaded';

    // track prev saleStartTs
    const saleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const prevSaleStartTs = usePrevious(saleStartTs);
    const saleStartHasChanged = prevSaleStartTs && prevSaleStartTs !== saleStartTs;

    // if true, fetchLiveCatalogStatusIfNeeded will be called on an interval
    const checkCatalogStatus = auctionHasUnLoaded || saleStartHasChanged;

    // Fetch catalog
    React.useEffect(() => {
        dispatch(fetchCatalogsIfNeeded({ catalogIds: [catalogId] }));
    }, [catalogId, dispatch]);

    // Fetch live catalog status every 10 seconds if auction switches to 'notLoaded' or saleStartTs changes
    useInterval(
        () => {
            dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId, true));
        },
        checkCatalogStatus ? 10000 : null
    );
};

export default useAuctionStatusChecker;
